import {
  defined,
  ScreenSpaceEventType,
  ScreenSpaceEventHandler,
} from "@cesium/engine";

import MeasurementAltitude from "../measurement/MeasurementAltitude";
import Utils from "../basic/Utils";

/**
 * 고도 측정을 위한 사용자 입력(마우스/키보드) 클래스.</br>
 * User input class for altitude measurement using mouse/keyboard input.
 *
 * @alias InteractionAltitude
 * @constructor
 *
 * @param {object} altitudeOption 다음과 같은 속성을 가진 객체.</br> Object with the following properties:
 * @param {Function} [altitudeOption.addCallback] 점을 생성할 때 실행되는 콜백 함수. <br>Callback function executed when creating a point.
 * @param {MeasurementAltitude} [altitudeOption.measurementAltitude] 고도 계산 및 영역 표시 클래스. </br> Altitude calculation and area display class.
 * @example
 * // 샌드캐슬 예제 'DiTAP - Altitude Measurement' 참조
 *
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const measurementAltitude = new Ditap.MeasurementAltitude(viewer);
 *
 * // InteractionAltitude on/off 실행 시 MeasurementAltitude 초기화 필수
 * // measurementAltitude 초기화 방법
 * // 1. InteractionAltitude 생성 후, MeasurementAltitude, callback 함수 대입
 * const interactionAltitude = new Ditap.InteractionAltitude();
 * interactionAltitude.measurementAltitude = measurementAltitude;
 * interactionAltitude.addCallback = () => {};
 *
 * // 2. InteractionAltitude 생성 시, altitudeOption 지정
 * const altitudeOption = {
 *    measurementAltitude: measurementAltitude,
 *    addCallback: () => {}
 * };
 * const interactionAltitude = new Ditap.InteractionAltitude(altitudeOption);
 */
class InteractionAltitude {
  constructor(altitudeOption) {
    this._measurementAltitude = undefined;
    if (
      altitudeOption &&
      altitudeOption.measurementAltitude instanceof MeasurementAltitude
    ) {
      this._measurementAltitude = altitudeOption.measurementAltitude; // 원본 클래스
    }
    this._handler = undefined;
    this._infoLabelEntity = undefined;
    this._mousePosition = undefined;
    this._isOn = false; // 핸들러 실행 여부
    this._buttonElement = undefined;
    this._keyDownEvent = undefined;
    this.cancelBtn = undefined;
    this._isMobile = undefined; // 모바일 여부

    if (altitudeOption && altitudeOption.addCallback instanceof Function) {
      this._addCallback = altitudeOption.addCallback;
    } else {
      this._addCallback = undefined;
    }
  }

  /**
   * 고도 계산 및 영역 표시 클래스.</br>
   * Altitude calculation and area display class.
   * @type {MeasurementAltitude}
   */
  set measurementAltitude(measurementAltitude) {
    if (
      measurementAltitude instanceof MeasurementAltitude ||
      measurementAltitude === undefined
    ) {
      this._measurementAltitude = measurementAltitude; // 원본 클래스
    } else {
      console.error("Type of parameter doesn't match");
    }
  }
  get measurementAltitude() {
    return this._measurementAltitude;
  }

  /*
   * 정보를 제공하는 라벨 요소.</br>
   * Label element providing information.
   * @type {Element}
   */
  set infoLabelEntity(infoLabelEntity) {
    this._infoLabelEntity = infoLabelEntity;
  }
  get infoLabelEntity() {
    return this._infoLabelEntity;
  }

  /*
   * 현재 마우스 좌표. </br>
   * Current mouse cartesian.
   * @type {Cartesian3}
   */
  set mousePosition(mousePosition) {
    this._mousePosition = mousePosition;
  }
  get mousePosition() {
    return this._mousePosition;
  }

  /*
   * 사용자 입력 이벤트 처리 핸들러.</br>
   * Handles user input events.
   * @type {ScreenSpaceEventHandler}
   */
  set handler(handler) {
    this._handler = handler;
  }
  get handler() {
    return this._handler;
  }

  /**
   * 핸들러 비/활성화.</br>
   * Handler enabled or disabled.
   * @type {boolean}
   * @readonly
   */
  set isOn(boolean) {
    this._isOn = boolean;
  }
  get isOn() {
    return this._isOn;
  }

  /*
   * 키 다운 시 실행 이벤트.</br>
   * Event to execute when a key is pressed.
   * @type {Function}
   */
  set keyDownEvent(keyDownEvent) {
    this._keyDownEvent = keyDownEvent;
  }
  get keyDownEvent() {
    return this._keyDownEvent;
  }

  /*
   * 사용자 입력 클래스 활성화 버튼.</br>
   * Button element that activates the user input class.
   * @type {Element}
   */
  set buttonElement(buttonElement) {
    this._buttonElement = buttonElement;
  }
  get buttonElement() {
    return this._buttonElement;
  }

  /**
   * 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating a point
   * @type {Function}
   */
  set addCallback(addCallback) {
    if (addCallback instanceof Function) {
      this._addCallback = addCallback;
    } else {
      this._addCallback = undefined;
    }
  }
  get addCallback() {
    return this._addCallback;
  }

  /**
   * 고도 측정 핸들러 실행. (MeasurementAltitude 초기화 필수.)</br>
   * Execute altitude measurement handler. (MeasurementAltitude initialization is required.)
   */
  on() {
    if (!this.isOn && this.measurementAltitude) {
      this.isOn = true;
      this._isMobile = Utils.detectDeviceType();

      // 버튼 활성화
      this.measurementAltitude.viewer.currentEvent = this;
      this.buttonElement?.classList.add("on");

      this.handler = new ScreenSpaceEventHandler(
        this.measurementAltitude.viewer.canvas
      );

      this.mobileInfoLabel = Utils.createMobileInfoLabel(
        this.measurementAltitude.viewer.container.querySelector(
          ".cesium-viewer"
        ),
        "고도를 측정할 위치에",
        "왼 클릭 또는 터치 해주세요"
      );

      this.infoLabel = Utils.createInfoLabel(
        this.measurementAltitude.viewer.container.querySelector(
          ".cesium-viewer"
        ),
        "고도를 측정할 위치에 왼 클릭 해주세요"
      );

      if (this._isMobile) {
        this.cancelBtn = this.mobileInfoLabel.cancelBtn;
        this.infoLabelEntity = this.mobileInfoLabel.label;
        this.createMobileEvent();
        this.infoLabel.style.display = "none";
      } else {
        this.infoLabelEntity = this.infoLabel;
        this.createMouseMoveHandler();
        this.mobileInfoLabel.label.style.display = "none";
      }

      this.createLeftClickHandler();
      this.createRightClickHandler();
      this.createKeyDownHandler();
      this.createResizeEvent();
    }
  }

  /**
   * 고도 측정 핸들러 종료. (MeasurementAltitude 초기화 필수.)</br>
   * End altitude measurement handler. (MeasurementAltitude initialization is required.)
   */
  off() {
    if (this.measurementAltitude && this.isOn) {
      this.measurementAltitude.viewer.currentEvent = undefined;
      // 버튼 비활성화
      this.buttonElement?.classList.remove("on");

      // 도움말 라벨 제거.
      this.mobileInfoLabel.label.remove();
      this.infoLabel.remove();

      // 이벤트 제거
      if (this.handler) {
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this.handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
      }
      window.removeEventListener("keydown", this.keyDownEvent);

      if (this._isMobile) {
        this.removeMobileEvent();
      }

      if (this.resizeEvent) {
        window.removeEventListener("resize", this.resizeEvent);
      }

      this.handler = undefined;
      this.mousePosition = undefined;
      this.isOn = false; // 핸들러 실행 여부
      this.keyDownEvent = undefined;
      this.measurementAltitude = undefined;
      this.cancelBtn = undefined;
      this._isMobile = undefined;
      this.infoLabelEntity = undefined;
      this.mobileInfoLabel = undefined;
      this.infoLabel = undefined;
    }
  }

  /**
   * 측정 중 중단.</br>
   * Interrupt during measurement.
   */
  abort() {
    //measurementAltitude 삭제
    this.measurementAltitude?.destroy();
  }

  /**
   * 객체의 리소스 파괴.<br>
   * Destroy resources held by this object.
   */
  destroy() {
    this.abort();
    this.off();
  }

  /**
   * 모바일 전용 이벤트 추가.<br>
   */
  createMobileEvent() {
    this.cancelBtn.addEventListener(
      "click",
      (this.cancelEvent = () => {
        this.destroy();
      })
    );
  }

  /**
   * 모바일 전용 이벤트 제거.<br>
   * Remove mobile-specific event.
   */
  removeMobileEvent() {
    if (this.cancelEvent) {
      this.cancelBtn.removeEventListener("click", this.cancelEvent);
      this.cancelBtn = undefined;
      this.cancelEvent = undefined;
    }
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   */
  createResizeEvent() {
    const resizeOriginal = () => {
      if (this.isOn) {
        const isMobile = Utils.detectDeviceType();

        if (this._isMobile !== isMobile) {
          if (isMobile) {
            this._isMobile = true;

            this.cancelBtn = this.mobileInfoLabel.cancelBtn;
            this.infoLabelEntity = this.mobileInfoLabel.label;
            this.infoLabel.style.display = "none";
            this.mobileInfoLabel.label.style.display = "block";

            this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
            this.createMobileEvent();
          } else {
            this.removeMobileEvent();
            this._isMobile = false;
            this.infoLabelEntity = this.infoLabel;
            this.mobileInfoLabel.label.style.display = "none";
            this.infoLabel.style.display = "block";
            this.createMouseMoveHandler();
          }
        }
      }
    };

    this.resizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.resizeEvent);
  }

  /*
   * 마우스 왼 클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse left-click event handler.
   */
  createLeftClickHandler() {
    const handler = this.handler;
    handler.setInputAction((e) => {
      const position = Utils.pickPosition(
        e.position,
        this.measurementAltitude.viewer
      );

      if (defined(position)) {
        this.mousePosition = position;
        this.measurementAltitude.position = position;
        this.measurementAltitude.createAltitudeEntity();

        if (this.addCallback && this.measurementAltitude) {
          this.addCallback(this.measurementAltitude);
        }
        // 핸들러 종료;
        this.off();
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  /*
   * 마우스 이동 이벤트 핸들러 생성.</br>
   * Creates a mouse move event handler.
   */
  createMouseMoveHandler() {
    const viewer = this.measurementAltitude.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      const position = Utils.pickPosition(e.endPosition, viewer);
      if (defined(position)) {
        this.mousePosition = position;

        if (this.infoLabelEntity) {
          // 도움말 라벨 좌표 이동
          Utils.changeLabelLocation(viewer, position, this.infoLabelEntity);
        }
      } else {
        this.infoLabelEntity.classList.add("hide");
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }

  /*
   * 마우스 우클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse right-click event handler.
   */
  createRightClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      this.abort();
      this.off();
    }, ScreenSpaceEventType.RIGHT_CLICK);
  }

  /*
   * 키 다운 이벤트 발생 시 실행.</br>
   * Executed when a key down event occurs.
   * @param {Event} event 키 다운 이벤트 객체.</br> keydown event object
   */
  onKeyDown(event) {
    if (event.key === "Escape") {
      // 동적 Entity 제거
      this.destroy();
    }
  }

  /*
   * 키 다운 이벤트 핸들러 생성.</br>
   * Creates a key down event handler.
   */
  createKeyDownHandler() {
    window.addEventListener(
      "keydown",
      (this.keyDownEvent = (e) => {
        this.onKeyDown(e);
      })
    );
  }
}
export default InteractionAltitude;
